import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Edit,
  Trash2,
  Eye,
  Download,
  Plus,
  Filter,
  CheckCircle,
  XCircle,
  UserPlus,
  UserMinus,
  RefreshCw,
  AlertTriangle,
  MoreVertical,
  ChevronDown,
  Shield,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Calendar,
  TrendingUp,
  TrendingDown,
  Check,
  X,
  PhoneCall,
  Smartphone,
  MessageCircle,
  Globe,
  MapPin,
  CreditCard
} from 'lucide-react';
import Button from '../../../components/common/Button';
import Card from '../../../components/ui/Card';
import DataTable from '../../../components/ui/DataTable';
import Input from '../../../components/common/Input';
import Select from '../../../components/common/Select';
import Badge from '../../../components/common/Badge';
import Avatar from '../../../components/Avatar';
import ConfirmModal from '../../../components/ConfirmModal';
import Toast from '../../../components/Toast';
import { useAuthStore } from '../../../store/store';
import UserService from '../../../firebase/users.service';

// Create a users store
import { create } from 'zustand';

export const useUsersStore = create((set, get) => ({
  users: [],
  loading: false,
  error: null,
  selectedUsers: [],
  totalUsers: 0,
  activeUsers: 0,
  pendingUsers: 0,
  suspendedUsers: 0,
  usersWithPhone: 0,
  verifiedUsers: 0,
  searchTerm: '',
  filters: {
    status: 'all',
    role: 'all',
    phoneStatus: 'all',
    verificationStatus: 'all'
  },
  
  // Actions
  fetchUsers: async () => {
    set({ loading: true, error: null });
    try {
      const users = await UserService.getAllUsers();
      
      // Process users to format dates and calculate stats
      const processedUsers = users.map(user => ({
        ...user,
        createdAt: user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt || Date.now()),
        lastLoginAt: user.lastLoginAt?.toDate ? user.lastLoginAt.toDate() : new Date(user.lastLoginAt || Date.now()),
        updatedAt: user.updatedAt?.toDate ? user.updatedAt.toDate() : new Date(user.updatedAt || Date.now())
      }));
      
      const activeUsers = processedUsers.filter(u => u.status === 'active').length;
      const pendingUsers = processedUsers.filter(u => u.status === 'pending').length;
      const suspendedUsers = processedUsers.filter(u => u.status === 'suspended').length;
      const usersWithPhone = processedUsers.filter(u => u.phone).length;
      const verifiedUsers = processedUsers.filter(u => u.isVerified).length;
      
      set({ 
        users: processedUsers, 
        loading: false, 
        totalUsers: processedUsers.length,
        activeUsers,
        pendingUsers,
        suspendedUsers,
        usersWithPhone,
        verifiedUsers
      });
    } catch (error) {
      set({ 
        loading: false, 
        error: error.message || 'Failed to fetch users' 
      });
    }
  },
  
  updateUserStatus: async (userId, status) => {
    set({ loading: true, error: null });
    try {
      await UserService.updateUserStatus(userId, status);
      
      const { users } = get();
      const updatedUsers = users.map(user => 
        user.id === userId ? { ...user, status } : user
      );
      
      // Recalculate counts
      const activeUsers = updatedUsers.filter(u => u.status === 'active').length;
      const pendingUsers = updatedUsers.filter(u => u.status === 'pending').length;
      const suspendedUsers = updatedUsers.filter(u => u.status === 'suspended').length;
      
      set({ 
        users: updatedUsers,
        loading: false,
        activeUsers,
        pendingUsers,
        suspendedUsers
      });
      return { success: true, userId, status };
    } catch (error) {
      set({ 
        loading: false, 
        error: error.message || 'Failed to update user status' 
      });
      throw error;
    }
  },
  
  updateUserVerification: async (userId, verificationData) => {
    set({ loading: true, error: null });
    try {
      await UserService.updateUser(userId, verificationData);
      
      const { users } = get();
      const updatedUsers = users.map(user => 
        user.id === userId ? { ...user, ...verificationData } : user
      );
      
      // Recalculate verified users count
      const verifiedUsers = updatedUsers.filter(u => u.isVerified).length;
      
      set({ 
        users: updatedUsers,
        loading: false,
        verifiedUsers
      });
      return { success: true, userId };
    } catch (error) {
      set({ 
        loading: false, 
        error: error.message || 'Failed to update verification' 
      });
      throw error;
    }
  },
  
  updateUserPhone: async (userId, phoneData) => {
    set({ loading: true, error: null });
    try {
      await UserService.updateUser(userId, {
        phone: phoneData.phone,
        isMobileVerified: phoneData.isMobileVerified
      });
      
      const { users } = get();
      const updatedUsers = users.map(user => 
        user.id === userId ? { 
          ...user, 
          phone: phoneData.phone,
          isMobileVerified: phoneData.isMobileVerified
        } : user
      );
      
      // Recalculate phone count
      const usersWithPhone = updatedUsers.filter(u => u.phone).length;
      
      set({ 
        users: updatedUsers,
        loading: false,
        usersWithPhone
      });
      return { success: true, userId };
    } catch (error) {
      set({ 
        loading: false, 
        error: error.message || 'Failed to update phone' 
      });
      throw error;
    }
  },
  
  deleteUser: async (userId) => {
    set({ loading: true, error: null });
    try {
      await UserService.deleteUser(userId);
      
      const { users, selectedUsers } = get();
      const updatedUsers = users.filter(user => user.id !== userId);
      const updatedSelectedUsers = selectedUsers.filter(id => id !== userId);
      
      // Recalculate counts
      const activeUsers = updatedUsers.filter(u => u.status === 'active').length;
      const pendingUsers = updatedUsers.filter(u => u.status === 'pending').length;
      const suspendedUsers = updatedUsers.filter(u => u.status === 'suspended').length;
      const usersWithPhone = updatedUsers.filter(u => u.phone).length;
      const verifiedUsers = updatedUsers.filter(u => u.isVerified).length;
      
      set({ 
        users: updatedUsers,
        selectedUsers: updatedSelectedUsers,
        loading: false,
        totalUsers: updatedUsers.length,
        activeUsers,
        pendingUsers,
        suspendedUsers,
        usersWithPhone,
        verifiedUsers
      });
      return { success: true, userId };
    } catch (error) {
      set({ 
        loading: false, 
        error: error.message || 'Failed to delete user' 
      });
      throw error;
    }
  },
  
  setSelectedUsers: (selectedUsers) => set({ selectedUsers }),
  setSearchTerm: (searchTerm) => set({ searchTerm }),
  setFilter: (filters) => set({ filters: { ...get().filters, ...filters } }),
  clearUsersError: () => set({ error: null }),
  clearFilters: () => set({ 
    searchTerm: '',
    filters: { status: 'all', role: 'all', phoneStatus: 'all', verificationStatus: 'all' }
  }),
  resetUsers: () => set({
    users: [],
    selectedUsers: [],
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0,
    suspendedUsers: 0,
    usersWithPhone: 0,
    verifiedUsers: 0,
    searchTerm: '',
    filters: { status: 'all', role: 'all', phoneStatus: 'all', verificationStatus: 'all' }
  })
}));

const UsersPage = () => {
  const navigate = useNavigate();
  
  // Use Zustand stores
  const { 
    users, 
    loading, 
    error, 
    selectedUsers, 
    totalUsers,
    activeUsers,
    pendingUsers,
    suspendedUsers,
    usersWithPhone,
    verifiedUsers,
    filters,
    fetchUsers,
    updateUserStatus,
    updateUserVerification,
    updateUserPhone,
    deleteUser,
    setSelectedUsers,
    setSearchTerm,
    setFilter,
    clearUsersError
  } = useUsersStore();
  
  const { user: currentUser } = useAuthStore();
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userToAction, setUserToAction] = useState(null);
  const [actionType, setActionType] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [editPhoneData, setEditPhoneData] = useState({ phone: '', isMobileVerified: false });
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle errors
  useEffect(() => {
    if (error) {
      setToastMessage(error);
      setToastType('error');
      setShowToast(true);
      setTimeout(() => {
        clearUsersError();
      }, 3000);
    }
  }, [error, clearUsersError]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(search);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [search, setSearchTerm]);

  const handleSelectUser = useCallback((userId, checked) => {
    let newSelectedUsers = [...selectedUsers];
    if (checked) {
      newSelectedUsers.push(userId);
    } else {
      newSelectedUsers = newSelectedUsers.filter(id => id !== userId);
    }
    setSelectedUsers(newSelectedUsers);
  }, [selectedUsers, setSelectedUsers]);

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      (user.name?.toLowerCase().includes(search.toLowerCase()) || 
       user.email?.toLowerCase().includes(search.toLowerCase()) ||
       (user.phone && user.phone.includes(search)) ||
       (user.city && user.city.toLowerCase().includes(search.toLowerCase())) ||
       (user.state && user.state.toLowerCase().includes(search.toLowerCase())));
    
    const matchesStatus = filters.status === 'all' || user.status === filters.status;
    const matchesRole = filters.role === 'all' || user.role === filters.role;
    
    // Phone status filter
    const matchesPhoneStatus = filters.phoneStatus === 'all' ? true :
      filters.phoneStatus === 'with-phone' ? user.phone :
      filters.phoneStatus === 'without-phone' ? !user.phone :
      filters.phoneStatus === 'verified' ? user.isMobileVerified : true;
    
    // Verification status filter
    const matchesVerificationStatus = filters.verificationStatus === 'all' ? true :
      filters.verificationStatus === 'verified' ? user.isVerified :
      filters.verificationStatus === 'pending' ? !user.isVerified : true;
    
    return matchesSearch && matchesStatus && matchesRole && matchesPhoneStatus && matchesVerificationStatus;
  });

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedUsers(filteredUsers.map(user => user.uid || user.id));
    } else {
      setSelectedUsers([]);
    }
  }, [filteredUsers, setSelectedUsers]);

  const handleViewUser = useCallback((userId) => {
    navigate(`/admin/users/${userId}`);
  }, [navigate]);

  const handleEditUser = useCallback((userId) => {
    navigate(`/admin/users/edit/${userId}`);
  }, [navigate]);

  const handleUserAction = useCallback((userId, action) => {
    const user = users.find(u => (u.uid || u.id) === userId);
    setUserToAction(user);
    setActionType(action);
    setShowConfirmModal(true);
  }, [users]);

  const handleEditPhone = useCallback((user) => {
    setUserToAction(user);
    setEditPhoneData({
      phone: user.phone || '',
      isMobileVerified: user.isMobileVerified || false
    });
    setShowPhoneModal(true);
  }, []);

  const handleToggleVerification = useCallback((user) => {
    setUserToAction(user);
    setActionType(user.isVerified ? 'unverify' : 'verify');
    setShowVerificationModal(true);
  }, []);

  const handleSavePhone = async () => {
    try {
      await updateUserPhone(userToAction.uid || userToAction.id, editPhoneData);
      setToastMessage('Phone number updated successfully');
      setToastType('success');
      setShowToast(true);
      setShowPhoneModal(false);
    } catch (error) {
      setToastMessage(`Failed to update phone: ${error.message}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleToggleVerificationConfirm = async () => {
    try {
      const newVerificationStatus = !userToAction.isVerified;
      await updateUserVerification(userToAction.uid || userToAction.id, {
        isVerified: newVerificationStatus,
        isEmailVerified: newVerificationStatus,
        isMobileVerified: newVerificationStatus,
        verificationStatus: newVerificationStatus ? 'verified' : 'pending'
      });
      setToastMessage(`User ${newVerificationStatus ? 'verified' : 'unverified'} successfully`);
      setToastType('success');
      setShowToast(true);
      setShowVerificationModal(false);
    } catch (error) {
      setToastMessage(`Failed to update verification: ${error.message}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  const confirmAction = async () => {
    try {
      const userId = userToAction.uid || userToAction.id;
      
      if (actionType === 'approve') {
        await updateUserStatus(userId, 'active');
        setToastMessage(`User ${userToAction.name} approved successfully`);
      } else if (actionType === 'suspend') {
        await updateUserStatus(userId, 'suspended');
        setToastMessage(`User ${userToAction.name} suspended successfully`);
      } else if (actionType === 'activate') {
        await updateUserStatus(userId, 'active');
        setToastMessage(`User ${userToAction.name} activated successfully`);
      } else if (actionType === 'reject') {
        await deleteUser(userId);
        setToastMessage(`User ${userToAction.name} rejected and removed`);
      } else if (actionType === 'delete') {
        await deleteUser(userId);
        setToastMessage(`User ${userToAction.name} deleted successfully`);
      }
      setToastType('success');
      setShowToast(true);
    } catch (error) {
      setToastMessage(`Failed to perform action: ${error.message}`);
      setToastType('error');
      setShowToast(true);
    } finally {
      setShowConfirmModal(false);
      setUserToAction(null);
      setActionType('');
    }
  };

  const getActionModalConfig = () => {
    switch (actionType) {
      case 'approve':
        return {
          title: 'Approve User',
          message: `Are you sure you want to approve ${userToAction?.name}?`,
          confirmText: 'Approve',
          variant: 'success'
        };
      case 'suspend':
        return {
          title: 'Suspend User',
          message: `Are you sure you want to suspend ${userToAction?.name}?`,
          confirmText: 'Suspend',
          variant: 'warning'
        };
      case 'activate':
        return {
          title: 'Activate User',
          message: `Are you sure you want to activate ${userToAction?.name}?`,
          confirmText: 'Activate',
          variant: 'success'
        };
      case 'reject':
        return {
          title: 'Reject User',
          message: `Are you sure you want to reject and remove ${userToAction?.name}? This action cannot be undone.`,
          confirmText: 'Reject',
          variant: 'danger'
        };
      case 'delete':
        return {
          title: 'Delete User',
          message: `Are you sure you want to delete ${userToAction?.name}? This action cannot be undone.`,
          confirmText: 'Delete',
          variant: 'danger'
        };
      case 'verify':
        return {
          title: 'Verify User',
          message: `Are you sure you want to verify ${userToAction?.name}?`,
          confirmText: 'Verify',
          variant: 'success'
        };
      case 'unverify':
        return {
          title: 'Unverify User',
          message: `Are you sure you want to unverify ${userToAction?.name}?`,
          confirmText: 'Unverify',
          variant: 'warning'
        };
      default:
        return { title: '', message: '', confirmText: 'Confirm', variant: 'primary' };
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'No phone';
    // Format +91 6309376010 to +91 63093 76010
    if (phone.startsWith('+91') && phone.length === 13) {
      return `${phone.slice(0, 3)} ${phone.slice(3, 8)} ${phone.slice(8)}`;
    }
    return phone;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (typeof date === 'object' && date.toDate) {
      date = date.toDate();
    }
    if (date instanceof Date) {
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
    return new Date(date).toLocaleDateString();
  };

  const columns = [
    {
      id: 'select',
      header: (
        <input
          type="checkbox"
          checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
      accessor: 'uid',
      render: (item) => (
        <input
          type="checkbox"
          checked={selectedUsers.includes(item.uid || item.id)}
          onChange={(e) => handleSelectUser(item.uid || item.id, e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
      width: '50px'
    },
    {
      id: 'user',
      header: 'User',
      accessor: 'name',
      render: (item) => (
        <div className="flex items-center">
          <Avatar 
            name={item.name}
            size="md"
            className="mr-3"
          />
          <div className="min-w-0">
            <div className="flex items-center">
              <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
              {item.role === 'admin' && (
                <Shield className="h-3 w-3 ml-2 text-purple-600" />
              )}
              {item.isVerified && (
                <Badge variant="success" size="xs" className="ml-2">
                  <Check className="h-2 w-2 mr-1" />
                  Verified
                </Badge>
              )}
            </div>
            <div className="flex items-center text-sm text-gray-500">
              <Mail className="h-3 w-3 mr-1" />
              <span className="truncate">{item.email}</span>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'phone',
      header: 'Contact Number',
      render: (item) => (
        <div className="min-w-0">
          {item.phone ? (
            <div>
              <div className="flex items-center">
                <Smartphone className="h-3 w-3 mr-2 text-gray-400" />
                <span className="font-medium text-gray-900">{formatPhoneNumber(item.phone)}</span>
              </div>
              <div className="flex items-center mt-1">
                {item.isMobileVerified ? (
                  <Badge variant="success" size="xs" className="mr-2">
                    <Check className="h-2 w-2 mr-1" />
                    Verified
                  </Badge>
                ) : (
                  <Badge variant="warning" size="xs" className="mr-2">
                    <AlertTriangle className="h-2 w-2 mr-1" />
                    Unverified
                  </Badge>
                )}
                <div className="flex space-x-1">
                  <a
                    href={`tel:${item.phone}`}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="Call"
                  >
                    <PhoneCall className="h-3 w-3" />
                  </a>
                  <a
                    href={`https://wa.me/${item.phone.replace('+', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-800 p-1"
                    title="WhatsApp"
                  >
                    <MessageCircle className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center text-gray-400">
              <Smartphone className="h-3 w-3 mr-2" />
              <span className="text-sm">No phone number</span>
            </div>
          )}
        </div>
      )
    },
    {
      id: 'location',
      header: 'Location',
      render: (item) => (
        <div className="min-w-0">
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="truncate">
              {item.city && item.state ? `${item.city}, ${item.state}` : 
               item.city || item.state || 'Not provided'}
            </span>
          </div>
          {item.country && (
            <div className="flex items-center text-xs text-gray-500 mt-1">
              <Globe className="h-2 w-2 mr-1" />
              {item.country}
            </div>
          )}
        </div>
      )
    },
    {
      id: 'role',
      header: 'Role',
      accessor: 'role',
      render: (item) => (
        <Badge 
          variant={
            item.role === 'admin' ? 'purple' :
            item.role === 'seller' ? 'blue' : 'gray'
          }
          size="sm"
          className="capitalize"
        >
          {item.role || 'buyer'}
        </Badge>
      )
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      render: (item) => (
        <div className="flex items-center">
          <Badge 
            variant={
              item.status === 'active' ? 'success' :
              item.status === 'pending' ? 'warning' :
              item.status === 'suspended' ? 'danger' : 'gray'
            }
            size="sm"
            className="capitalize"
          >
            <span className="flex items-center">
              {item.status === 'active' && <CheckCircle className="h-3 w-3 mr-1" />}
              {item.status === 'pending' && <AlertTriangle className="h-3 w-3 mr-1" />}
              {item.status === 'suspended' && <UserX className="h-3 w-3 mr-1" />}
              {item.status || 'Unknown'}
            </span>
          </Badge>
        </div>
      )
    },
    {
      id: 'joined',
      header: 'Joined',
      accessor: 'createdAt',
      render: (item) => (
        <div className="flex flex-col text-sm">
          <div className="text-gray-900">{formatDate(item.createdAt)}</div>
          {item.lastLoginAt && (
            <div className="text-xs text-gray-500 mt-1">
              Last login: {formatDate(item.lastLoginAt)}
            </div>
          )}
        </div>
      )
    },
    {
      id: 'profile',
      header: 'Profile',
      render: (item) => (
        <div className="space-y-1">
          <div className="flex items-center text-sm">
            <div className="w-16 bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-green-600 h-1.5 rounded-full" 
                style={{ width: `${item.profilePercentage || 0}%` }}
              />
            </div>
            <span className="ml-2 text-xs text-gray-600">{item.profilePercentage || 0}%</span>
          </div>
          {item.totalTransactions > 0 && (
            <div className="flex items-center text-xs text-purple-600">
              <CreditCard className="h-2 w-2 mr-1" />
              {item.totalTransactions} transactions
            </div>
          )}
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      render: (item) => {
        const isDropdownOpen = dropdownOpen === item.uid;
        return (
          <div className="relative">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => setDropdownOpen(isDropdownOpen ? null : item.uid)}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
                
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                    <div className="py-1">
                      {/* View Action */}
                      {/* <button
                        onClick={() => handleViewUser(item.uid || item.id)}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </button> */}
                      
                      {/* Phone Action */}
                      <button
                        onClick={() => handleEditPhone(item)}
                        className="flex items-center w-full px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                      >
                        <Smartphone className="h-4 w-4 mr-2" />
                        {item.phone ? 'Edit Phone' : 'Add Phone'}
                      </button>
                      
                      {/* Verification Action */}
                      <button
                        onClick={() => handleToggleVerification(item)}
                        className="flex items-center w-full px-4 py-2 text-sm text-purple-700 hover:bg-purple-50"
                      >
                        {item.isVerified ? (
                          <>
                            <XCircle className="h-4 w-4 mr-2" />
                            Unverify User
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Verify User
                          </>
                        )}
                      </button>
                      
                      {/* Call Action */}
                      {item.phone && (
                        <>
                          <a
                            href={`tel:${item.phone}`}
                            className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                          >
                            <PhoneCall className="h-4 w-4 mr-2" />
                            Call User
                          </a>
                          <a
                            href={`https://wa.me/${item.phone.replace('+', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center w-full px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                          >
                            <MessageCircle className="h-4 w-4 mr-2" />
                            WhatsApp
                          </a>
                        </>
                      )}
                      
                      <div className="border-t border-gray-200 my-1"></div>
                      
                      {/* Status Actions */}
                      {item.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleUserAction(item.uid || item.id, 'approve')}
                            className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve User
                          </button>
                          <button
                            onClick={() => handleUserAction(item.uid || item.id, 'reject')}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject User
                          </button>
                        </>
                      )}
                      
                      {item.status === 'active' && (item.uid !== currentUser?.uid) && (
                        <button
                          onClick={() => handleUserAction(item.uid || item.id, 'suspend')}
                          className="flex items-center w-full px-4 py-2 text-sm text-yellow-700 hover:bg-yellow-50"
                        >
                          <UserMinus className="h-4 w-4 mr-2" />
                          Suspend User
                        </button>
                      )}
                      
                      {item.status === 'suspended' && (
                        <button
                          onClick={() => handleUserAction(item.uid || item.id, 'activate')}
                          className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Activate User
                        </button>
                      )}
                      
                      {(item.uid !== currentUser?.uid) && (
                        <button
                          onClick={() => handleUserAction(item.uid || item.id, 'delete')}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete User
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }
    }
  ];

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) return;
    
    try {
      if (action === 'activate') {
        for (const userId of selectedUsers) {
          await updateUserStatus(userId, 'active');
        }
        setToastMessage(`${selectedUsers.length} users activated successfully`);
      } else if (action === 'suspend') {
        for (const userId of selectedUsers) {
          const user = users.find(u => (u.uid || u.id) === userId);
          if (user && (user.uid !== currentUser?.uid)) {
            await updateUserStatus(userId, 'suspended');
          }
        }
        setToastMessage(`${selectedUsers.length} users suspended successfully`);
      } else if (action === 'delete') {
        for (const userId of selectedUsers) {
          const user = users.find(u => (u.uid || u.id) === userId);
          if (user && (user.uid !== currentUser?.uid)) {
            await deleteUser(userId);
          }
        }
        setToastMessage(`${selectedUsers.length} users deleted successfully`);
      }
      setToastType('success');
      setShowToast(true);
      setSelectedUsers([]);
      setDropdownOpen(null);
    } catch (error) {
      setToastMessage(`Failed to perform bulk action: ${error.message}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleStatusFilter = (value) => {
    setFilter({ status: value });
  };

  const handleRoleFilter = (value) => {
    setFilter({ role: value });
  };

  const handlePhoneFilter = (value) => {
    setFilter({ phoneStatus: value });
  };

  const handleVerificationFilter = (value) => {
    setFilter({ verificationStatus: value });
  };

  const UserStatsCard = ({ title, value, change, icon: Icon, color }) => (
    <Card className={`border-l-4 border-l-${color}-500 hover:shadow-lg transition-shadow duration-200`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change > 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
              <span>{Math.abs(change)}% from last month</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-50`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </Card>
  );

  // Update stats cards with real data
  const statsCards = [
    {
      title: 'Total Users',
      value: totalUsers,
      change: 12.5,
      icon: Users,
      color: 'blue'
    },
    {
      title: 'With Phone',
      value: usersWithPhone,
      change: usersWithPhone > 0 ? 8.2 : 0,
      icon: Smartphone,
      color: 'green'
    },
    {
      title: 'Verified Users',
      value: verifiedUsers,
      change: verifiedUsers > 0 ? 15.3 : 0,
      icon: CheckCircle,
      color: 'purple'
    },
    {
      title: 'Active Users',
      value: activeUsers,
      change: activeUsers > 0 ? 5.7 : 0,
      icon: UserCheck,
      color: 'green'
    }
  ];

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Toast Notification */}
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setUserToAction(null);
          setActionType('');
          setDropdownOpen(null);
        }}
        onConfirm={confirmAction}
        title={getActionModalConfig().title}
        message={getActionModalConfig().message}
        confirmText={getActionModalConfig().confirmText}
        variant={getActionModalConfig().variant}
      />

      {/* Verification Modal */}
      <ConfirmModal
        isOpen={showVerificationModal}
        onClose={() => {
          setShowVerificationModal(false);
          setUserToAction(null);
          setActionType('');
        }}
        onConfirm={handleToggleVerificationConfirm}
        title={getActionModalConfig().title}
        message={getActionModalConfig().message}
        confirmText={getActionModalConfig().confirmText}
        variant={getActionModalConfig().variant}
      />

      {/* Phone Edit Modal */}
      <ConfirmModal
        isOpen={showPhoneModal}
        onClose={() => {
          setShowPhoneModal(false);
          setUserToAction(null);
        }}
        onConfirm={handleSavePhone}
        title={`Edit Phone for ${userToAction?.name}`}
        message={
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <Input
                type="tel"
                value={editPhoneData.phone}
                onChange={(e) => setEditPhoneData({...editPhoneData, phone: e.target.value})}
                placeholder="+91 1234567890"
                className="w-full"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="phoneVerified"
                checked={editPhoneData.isMobileVerified}
                onChange={(e) => setEditPhoneData({...editPhoneData, isMobileVerified: e.target.checked})}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <label htmlFor="phoneVerified" className="ml-2 text-sm text-gray-700">
                Mark as Verified
              </label>
            </div>
          </div>
        }
        confirmText="Save Phone"
        variant="primary"
      />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            Manage and monitor all user accounts with contact information
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="small"
            onClick={() => fetchUsers()}
            loading={loading}
            className="flex-1 sm:flex-none"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((stat, index) => (
          <UserStatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg mr-3">
                <AlertTriangle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-blue-800">
                  {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
                </p>
                <p className="text-xs text-blue-600">Choose an action to perform on selected users</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="small"
                variant="success"
                onClick={() => handleBulkAction('activate')}
                className="flex-1 sm:flex-none"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Activate</span>
              </Button>
              <Button
                size="small"
                variant="warning"
                onClick={() => handleBulkAction('suspend')}
                className="flex-1 sm:flex-none"
              >
                <UserMinus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Suspend</span>
              </Button>
              <Button
                size="small"
                variant="danger"
                onClick={() => handleBulkAction('delete')}
                className="flex-1 sm:flex-none"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Delete</span>
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Filters Card */}
      <Card>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name, email, phone, city, state..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center space-x-2">
              <Select
                value={filters.status}
                onChange={(e) => handleStatusFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'suspended', label: 'Suspended' }
                ]}
                className="min-w-[140px]"
              />
              <Select
                value={filters.role}
                onChange={(e) => handleRoleFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'All Roles' },
                  { value: 'buyer', label: 'Buyers' },
                  { value: 'seller', label: 'Sellers' },
                  { value: 'admin', label: 'Admins' }
                ]}
                className="min-w-[140px]"
              />
              <Select
                value={filters.phoneStatus}
                onChange={(e) => handlePhoneFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'All Phones' },
                  { value: 'with-phone', label: 'With Phone' },
                  { value: 'without-phone', label: 'Without Phone' },
                  { value: 'verified', label: 'Verified Phones' }
                ]}
                className="min-w-[140px]"
              />
              <Select
                value={filters.verificationStatus}
                onChange={(e) => handleVerificationFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'All Verification' },
                  { value: 'verified', label: 'Verified' },
                  { value: 'pending', label: 'Pending' }
                ]}
                className="min-w-[140px]"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="small">
                <Download className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Users Content */}
      {loading && users.length === 0 ? (
        <Card className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading users...</p>
          </div>
        </Card>
      ) : filteredUsers.length === 0 ? (
        <Card className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No users found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filters</p>
          <Button variant="outline" onClick={() => {
            setSearch('');
            setFilter({ 
              status: 'all', 
              role: 'all', 
              phoneStatus: 'all',
              verificationStatus: 'all' 
            });
          }}>
            Clear all filters
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <DataTable
              columns={columns}
              data={filteredUsers}
              loading={loading}
              itemsPerPage={10}
              emptyMessage="No users found. Try adjusting your filters."
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-700 mb-2 sm:mb-0">
              Showing <span className="font-semibold">1-{Math.min(filteredUsers.length, 10)}</span> of{' '}
              <span className="font-semibold">{filteredUsers.length}</span> users
            </p>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="xs" disabled>
                Previous
              </Button>
              <Button variant="outline" size="xs">
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default UsersPage;